/**
 * MOCK/TEMPORARY — support tickets and FAQ content.
 *
 * Submitting a ticket adds a row to a client-side list and shows a toast saying
 * the team will follow up by email. No email is sent and no ticket is recorded
 * anywhere. FAQ/guide content is static fixture data.
 *
 * Real endpoints: docs/api-contract.md, "Support tickets".
 */
import { USE_MOCK_API } from '../config';
import { FAQ_TOPICS } from '../data/faq';
import { newId } from '../lib/ids';
import type { FaqTopic, SupportTicket } from '../types/domain';
import { ApiError } from './errors';
import { http } from './http';
import { assertMock, mockDelay } from './mock';

/**
 * The one seed ticket the prototype shipped with, so the ticket list is not
 * empty on first view.
 */
export function createInitialTickets(): SupportTicket[] {
  return [
    {
      id: newId(),
      subject: "Can't download WAV export",
      category: 'Song generation',
      status: 'Resolved',
      date: 'Jul 24',
    },
  ];
}

export const supportApi = {
  /** MOCK: GET /support/faq */
  async getFaqTopics(): Promise<FaqTopic[]> {
    if (!USE_MOCK_API) {
      return http.get<FaqTopic[]>('/support/faq', { auth: false });
    }
    assertMock('supportApi.getFaqTopics');
    return mockDelay(FAQ_TOPICS);
  },

  /** MOCK: GET /support/tickets */
  async getTickets(): Promise<SupportTicket[]> {
    if (!USE_MOCK_API) {
      return http.get<SupportTicket[]>('/support/tickets');
    }
    assertMock('supportApi.getTickets');
    return mockDelay(createInitialTickets());
  },

  /** MOCK: POST /support/tickets */
  async createTicket(input: {
    subject: string;
    category: string;
    message: string;
    email?: string;
    authenticated?: boolean;
  }): Promise<SupportTicket> {
    if (!USE_MOCK_API) {
      const { authenticated = true, ...body } = input;
      return http.post<SupportTicket>('/support/tickets', body, { auth: authenticated });
    }
    assertMock('supportApi.createTicket');
    if (!input.subject.trim() || !input.message.trim()) {
      throw new ApiError(422, 'Add a subject and message first');
    }
    if (!input.authenticated && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email?.trim() ?? '')) {
      throw new ApiError(422, 'Enter a valid email address.');
    }
    return mockDelay({
      id: newId(),
      subject: input.subject.trim(),
      category: input.category,
      message: input.message.trim(),
      status: 'Open',
      date: 'Just now',
    });
  },
};
